import type { StepTransitionPhase } from "./form-types";

const STEP_TRANSITION_PHASE = {
  delaying: "delaying",
  entering: "entering",
  exiting: "exiting",
  idle: "idle",
} as const satisfies Record<string, StepTransitionPhase>;

type StepTransitionTiming = {
  delayBeforeExitMs: number;
  enterDurationMs: number;
  exitDurationMs: number;
};

type RunStepTransitionSequenceOptions = {
  /** Skips the configured delay before the exit starts. Used by Back, which never waits. */
  skipDelay?: boolean;
};

type RunStepTransitionSequenceCallbacks = {
  advance: () => void;
  onPhaseChange: (phase: StepTransitionPhase) => void;
  schedule: (callback: () => void, delayMs: number) => void;
};

function isStepTransitionExiting(phase: StepTransitionPhase): boolean {
  return phase === STEP_TRANSITION_PHASE.exiting;
}

/**
 * Schedules the exit → swap → enter sequence for an animated step transition.
 * Each phase change is explicit so callers can step through the state machine.
 */
function runStepTransitionSequence(
  timing: StepTransitionTiming,
  options: RunStepTransitionSequenceOptions | undefined,
  callbacks: RunStepTransitionSequenceCallbacks,
): void {
  const holdMs = options?.skipDelay ? 0 : timing.delayBeforeExitMs;
  const { onPhaseChange, schedule, advance } = callbacks;

  if (holdMs === 0) {
    onPhaseChange(STEP_TRANSITION_PHASE.exiting);
  } else {
    onPhaseChange(STEP_TRANSITION_PHASE.delaying);
    schedule(() => onPhaseChange(STEP_TRANSITION_PHASE.exiting), holdMs);
  }

  // The step swaps while the grid is at zero opacity, so the incoming step
  // is never visible in the outgoing step's position.
  schedule(() => {
    advance();
    onPhaseChange(STEP_TRANSITION_PHASE.entering);
  }, holdMs + timing.exitDurationMs);

  schedule(
    () => onPhaseChange(STEP_TRANSITION_PHASE.idle),
    holdMs + timing.exitDurationMs + timing.enterDurationMs,
  );
}

export {
  isStepTransitionExiting,
  runStepTransitionSequence,
  STEP_TRANSITION_PHASE,
};
